import httpClient from '@/config/httpClient';
import firebase from '../config/firebase.config';
import teamDatabase from './team';

const db = firebase.firestore();
const FieldValue = firebase.firestore.FieldValue;

async function sha256HexUtf8(plain) {
  const enc = new TextEncoder().encode(plain);
  const buf = await crypto.subtle.digest('SHA-256', enc);
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

async function tokenMatchesStoredHash(plainToken, storedHashHex) {
  if (!plainToken || !storedHashHex) return false;
  const computed = await sha256HexUtf8(plainToken);
  return computed === storedHashHex;
}

function emailsMatch(a, b) {
  if (!a || !b) return false;
  return String(a).trim().toLowerCase() === String(b).trim().toLowerCase();
}

/**
 * Query pending `invites` for an email (e.g. after login — user may not have the `t` token).
 */
export async function findPendingInvitesForEmail(email) {
  const e = email?.trim().toLowerCase();
  if (!e) return [];
  try {
    const snap = await db
      .collection('invites')
      .where('inviteeEmail', '==', e)
      .where('status', '==', 'pending')
      .orderBy('createdAt', 'desc')
      .limit(5)
      .get();
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (err) {
    console.error('[findPendingInvitesForEmail]', err);
    return [];
  }
}

async function assertInviteAccessForAction(inv, plainToken, user) {
  if (!user?.email) {
    return { ok: false, message: 'Please sign in.' };
  }
  if (!emailsMatch(user.email, inv.inviteeEmail)) {
    return {
      ok: false,
      message: 'Sign in with the email address this invitation was sent to.',
    };
  }
  const hasToken = plainToken && String(plainToken).trim();
  if (hasToken) {
    const valid = await tokenMatchesStoredHash(
      String(plainToken).trim(),
      inv.tokenHash
    );
    if (!valid) {
      return { ok: false, message: 'Invalid invitation link.' };
    }
    return { ok: true };
  }
  return { ok: true };
}

function normalizeInviteSnapshot(docSnap) {
  const data = docSnap.data();
  const id = docSnap.id;
  return {
    ...data,
    id,
    _inviteSource: 'invites',
    accepted: data.status === 'accepted',
    declined: data.status === 'declined',
    userDetails: {
      email: data.inviteeEmail,
      registered: data.inviteeRegistered === true,
    },
    invitedBy: {
      fullName: data.invitedByEmail || 'Organizer',
      companyDetails: {
        companyId: data.companyId,
        companyName: data.companyName,
      },
    },
    invitedTo:
      data.scopeType === 'team'
        ? {
            teamId: data.teamId,
            teamName: data.teamName,
          }
        : {
            teamId: data.contextTeamId || null,
            teamName: data.contextTeamName || data.companyName,
          },
    invitedAs: data.invitedAs,
  };
}

/** Company / team / role lines for accept & decline notification emails. */
function buildInviteNotifyEmailFields(inv) {
  let teamDetailsLine = '—';
  if (inv.scopeType === 'team' && inv.teamId) {
    const teamLabel = inv.teamName || inv.teamId;
    teamDetailsLine = `Team: ${teamLabel}`;
  } else if (inv.scopeType === 'company_admin') {
    teamDetailsLine = inv.contextTeamName
      ? `Context: ${inv.contextTeamName}`
      : inv.companyName || '—';
  }
  const role =
    inv.invitedAs === 'Admin' ? 'Admin' : String(inv.invitedAs || '');
  return { teamDetailsLine, role };
}

/**
 * Resolves `invites/{id}` with token `t` **or** signed-in invitee email matching
 * `authEmail` (session path when user has no `t`).
 */
export async function resolveInvitation(inviteId, tokenFromQuery, authEmail) {
  if (!inviteId) {
    return { success: false, message: 'Missing invitation id' };
  }

  const snap = await db.collection('invites').doc(inviteId).get();
  if (!snap.exists) {
    return {
      success: false,
      message: 'No invitation found for this link.',
    };
  }

  const raw = snap.data();
  if (!tokenFromQuery) {
    if (authEmail && emailsMatch(authEmail, raw.inviteeEmail)) {
      return {
        success: true,
        source: 'invites',
        data: normalizeInviteSnapshot(snap),
      };
    }
    return {
      success: false,
      message:
        'This invitation link is incomplete. Please open the link from your email.',
    };
  }

  const ok = await tokenMatchesStoredHash(
    String(tokenFromQuery).trim(),
    raw.tokenHash
  );
  if (!ok) {
    return {
      success: false,
      message: 'Invalid or expired invitation link.',
    };
  }

  return {
    success: true,
    source: 'invites',
    data: normalizeInviteSnapshot(snap),
  };
}

export async function declineInviteFromInvites(inviteId, plainToken) {
  const user = firebase.auth().currentUser;
  if (!user?.email) {
    return { success: false, message: 'Please sign in to decline.' };
  }

  const inviteRef = db.collection('invites').doc(inviteId);
  const snap = await inviteRef.get();
  if (!snap.exists) {
    return { success: false, message: 'Invitation not found.' };
  }

  const inv = snap.data();
  if (inv.status !== 'pending') {
    return { success: false, message: 'This invitation is no longer pending.' };
  }

  const access = await assertInviteAccessForAction(inv, plainToken, user);
  if (!access.ok) {
    return { success: false, message: access.message };
  }

  const email = user.email.toLowerCase().trim();
  const recipientName =
    user.displayName || inv.inviteeName || email.split('@')[0];
  const { teamDetailsLine, role } = buildInviteNotifyEmailFields(inv);

  await inviteRef.update({
    status: 'declined',
    updatedAt: FieldValue.serverTimestamp(),
  });

  try {
    await teamDatabase.sendInviteDeclinedEmail({
      memberGuid: user.uid,
      recipientName,
      companyName: inv.companyName || '',
      role,
      teamDetailsLine,
      ...(inv.invitedByEmail
        ? {
            invitedByEmail: inv.invitedByEmail,
            ...(typeof inv.invitedByName === 'string' &&
            inv.invitedByName.trim()
              ? { invitedByName: inv.invitedByName.trim() }
              : {}),
          }
        : {}),
    });
  } catch (emailErr) {
    console.warn('[declineInviteFromInvites] decline email failed', emailErr);
  }

  return { success: true };
}

export async function revokeInvite(inviteId) {
  const inviteRef = db.collection('invites').doc(inviteId);
  const snap = await inviteRef.get();
  if (!snap.exists) return { success: false, message: 'Invitation not found.' };
  if (snap.data().status !== 'pending') {
    return {
      success: false,
      message: 'Only pending invitations can be revoked.',
    };
  }
  await inviteRef.update({
    status: 'revoked',
    updatedAt: FieldValue.serverTimestamp(),
  });
  return { success: true };
}

export async function resendInvite(inviteId) {
  const fn = firebase.functions().httpsCallable('resendInvite');
  const result = await fn({ inviteId });
  return result.data;
}

export async function acceptInviteFromInvites(inviteId, plainToken, user) {
  if (!user?.uid || !user.email) {
    return { success: false, message: 'Please sign in to accept.' };
  }

  const email = user.email.toLowerCase().trim();

  const inviteRef = db.collection('invites').doc(inviteId);
  const inviteSnap = await inviteRef.get();
  if (!inviteSnap.exists) {
    return { success: false, message: 'Invitation not found.' };
  }

  const inv = inviteSnap.data();
  const displayName =
    user.displayName || inv.inviteeName || email.split('@')[0];
  if (inv.status !== 'pending') {
    return { success: false, message: 'This invitation is no longer active.' };
  }

  const access = await assertInviteAccessForAction(inv, plainToken, user);
  if (!access.ok) {
    return { success: false, message: access.message };
  }

  const invitedAs = inv.invitedAs;
  const loginAllowed = invitedAs === 'Admin' || invitedAs === 'Manager';
  const uid = user.uid;
  const inviteName = inv.inviteeName || displayName;

  const memberRef = db.collection('membersDatabase').doc(uid);
  const companyRef = db.collection('companyDatabase').doc(inv.companyId);
  const memberCompanyRef = memberRef
    .collection('companyList')
    .doc(inv.companyId);

  const batch = db.batch();
  /** Payload for post-commit welcome email (Cloud Function). */
  let welcomeEmailPayload = null;

  batch.update(inviteRef, {
    status: 'accepted',
    updatedAt: FieldValue.serverTimestamp(),
    acceptedByUserId: uid,
  });
  let teamPayload = {
    selectedTeamID: null,
    activeUserID: null,
    activeTeam: {
      Team: null,
      activeTeamid: null,
    },
    companyId: null,
  };

  if (inv.scopeType === 'company_admin' && invitedAs === 'Admin') {
    const companySnap = await companyRef.get();
    if (!companySnap.exists) {
      return { success: false, message: 'Company was not found.' };
    }
    const allCompanyTeamIds = companySnap.data().teams || [];
    let teamIds = allCompanyTeamIds;
    if (Array.isArray(inv.restoreTeamIds) && inv.restoreTeamIds.length > 0) {
      const filtered = inv.restoreTeamIds.filter((tid) =>
        allCompanyTeamIds.includes(tid)
      );
      if (filtered.length > 0) {
        teamIds = filtered;
      }
    }
    const teamNamesForEmail = [];

    batch.set(
      memberRef,
      {
        accountEmailid: email,
        accountFullName: inviteName,
        desktopLogin: false,
        loginAllowed: true,
        activeCompanyId: inv.companyId,
        onboardingData: {
          onboardingCompleted: true,
          onboardingSteps: {},
        },
        ...(inv.timeZone ? { timeZone: inv.timeZone } : {}),
      },
      { merge: true }
    );

    batch.set(
      memberCompanyRef,
      {
        role: 'Admin',
        companyName: inv.companyName,
        companyId: inv.companyId,
      },
      { merge: true }
    );

    for (const tid of teamIds) {
      const teamRef = db.collection('teamDatabase').doc(tid);
      const teamSnap = await teamRef.get();
      if (!teamSnap.exists) continue;
      const teamName = teamSnap.data().teamName || tid;
      teamNamesForEmail.push(teamName);
      batch.set(
        memberCompanyRef.collection('teamList').doc(tid),
        {
          Role: 'Admin',
          Team: teamName,
          activeTeamid: tid,
          email,
          deleted: FieldValue.delete(),
          deletedAt: FieldValue.delete(),
        },
        { merge: true }
      );
      batch.update(teamRef, {
        [`memberDetails.${uid}`]: {
          userEmailid: email,
          userName: inviteName,
          userType: 'Admin',
        },
      });
    }

    batch.set(
      companyRef,
      {
        admins: FieldValue.arrayUnion(uid),
      },
      { merge: true }
    );

    welcomeEmailPayload = {
      memberGuid: uid,
      recipientName: inviteName,
      companyName: inv.companyName || '',
      role: 'Admin',
      teamDetailsLine:
        teamNamesForEmail.length > 0 ? teamNamesForEmail.join(', ') : '—',
      ...(inv.invitedByEmail
        ? {
            invitedByEmail: inv.invitedByEmail,
            ...(typeof inv.invitedByName === 'string' &&
            inv.invitedByName.trim()
              ? { invitedByName: inv.invitedByName.trim() }
              : {}),
          }
        : {}),
    };
  } else if (inv.scopeType === 'team' && inv.teamId) {
    const teamRef = db.collection('teamDatabase').doc(inv.teamId);
    const teamSnap = await teamRef.get();
    if (!teamSnap.exists) {
      return { success: false, message: 'Team was not found.' };
    }
    const teamLabel = inv.teamName || teamSnap.data().teamName || inv.teamId;

    batch.set(
      memberRef,
      {
        accountEmailid: email,
        accountFullName: inviteName,
        desktopLogin: false,
        loginAllowed,
        activeCompanyId: inv.companyId,
        activeTeamId: inv.teamId,
        onboardingData: {
          onboardingCompleted: true,
          onboardingSteps: {},
        },
        ...(inv.timeZone ? { timeZone: inv.timeZone } : {}),
      },
      { merge: true }
    );

    batch.set(
      memberCompanyRef,
      {
        role: invitedAs,
        companyName: inv.companyName,
        companyId: inv.companyId,
      },
      { merge: true }
    );

    batch.set(
      memberCompanyRef.collection('teamList').doc(inv.teamId),
      {
        Role: invitedAs,
        Team: teamLabel,
        activeTeamid: inv.teamId,
        email,
        deleted: FieldValue.delete(),
        deletedAt: FieldValue.delete(),
      },
      { merge: true }
    );

    batch.update(teamRef, {
      [`memberDetails.${uid}`]: {
        userEmailid: email,
        userName: inviteName,
        userType: invitedAs,
      },
    });

    if (invitedAs !== 'Admin') {
      teamPayload = {
        selectedTeamID: inv.teamId,
        activeUserID: uid,
        activeTeam: {
          Team: teamLabel,
          activeTeamid: inv.teamId,
        },
      };
      batch.set(
        companyRef.collection('teams').doc(inv.teamId),
        {
          members: FieldValue.arrayUnion(uid),
        },
        { merge: true }
      );
    }

    welcomeEmailPayload = {
      memberGuid: uid,
      recipientName: inviteName,
      companyName: inv.companyName || '',
      invitedByName: inv.invitedByName || '',
      role: String(invitedAs || ''),
      teamDetailsLine: `Team: ${teamLabel}`,
      ...(inv.invitedByEmail
        ? {
            invitedByEmail: inv.invitedByEmail,
            ...(typeof inv.invitedByName === 'string' &&
            inv.invitedByName.trim()
              ? { invitedByName: inv.invitedByName.trim() }
              : {}),
          }
        : {}),
    };
  } else {
    return {
      success: false,
      message: 'Unsupported invitation type.',
    };
  }

  try {
    const addSeatResponse = await httpClient.post('/addSubscriptionQuantity', {
      companyId: inv.companyId,
      additionalQuantity: 1,
    });
    if (addSeatResponse.data.success) {
      await batch.commit();
    } else {
      return {
        success: false,
        message:
          addSeatResponse.data.message ||
          'Could not complete acceptance. Try again.',
      };
    }
  } catch (e) {
    const message =
      e?.response?.data?.message || 'Could not complete acceptance. Try again.';
    return {
      success: false,
      message,
    };
  }

  if (welcomeEmailPayload) {
    try {
      await teamDatabase.sendInviteAcceptedWelcomeEmail(welcomeEmailPayload);
    } catch (emailErr) {
      console.warn('[acceptInviteFromInvites] welcome email failed', emailErr);
    }
  }

  return {
    success: true,
    companyId: inv.companyId,
    teamId: inv.scopeType === 'team' ? inv.teamId : null,
    teamPayload: inv.scopeType === 'team' ? teamPayload : null,
  };
}
